import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Sparkles, Check, AlertTriangle, Users, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ACTION_OPTS = [
  { v: "create", l: "Crea nuovo" },
  { v: "update_missing", l: "Completa campi vuoti" },
  { v: "update_overwrite", l: "Sostituisci con file" },
  { v: "ignore", l: "Ignora" },
  { v: "verify", l: "Verifica manualmente" },
];

const MATCH_LABEL = {
  new: { l: "Nuovo", c: "bg-emerald-100 text-emerald-800" },
  external_id: { l: "Match: external ID", c: "bg-blue-100 text-blue-800" },
  fis_code: { l: "Match: FIS", c: "bg-blue-100 text-blue-800" },
  name_birth: { l: "Match: nome+data", c: "bg-blue-100 text-blue-800" },
  email: { l: "Match: email", c: "bg-blue-100 text-blue-800" },
  phone: { l: "Match: telefono", c: "bg-blue-100 text-blue-800" },
  possible_duplicate: { l: "Possibile duplicato", c: "bg-amber-100 text-amber-800" },
};

export default function ImportAthletesV2Dialog({ open, onOpenChange, onDone }) {
  const [phase, setPhase] = useState("upload"); // upload / analyze / confirm / done
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [decisions, setDecisions] = useState({}); // row_id -> action
  const [busy, setBusy] = useState(false);

  const reset = () => { setPhase("upload"); setFile(null); setAnalysis(null); setDecisions({}); };

  const doAnalyze = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/athletes/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
      const decs = {};
      (data.rows || []).forEach((r) => { decs[r.row_id] = r.suggested_action; });
      setDecisions(decs);
      setPhase("analyze");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore analisi"); }
    finally { setBusy(false); }
  };

  const doConfirm = async () => {
    setBusy(true);
    try {
      const body = {
        session_id: analysis.session_id,
        decisions: Object.entries(decisions).map(([row_id, action]) => ({ row_id, action })),
      };
      const { data } = await api.post("/import/athletes/confirm", body);
      toast.success("Importazione completata");
      setAnalysis({ ...analysis, summary: data.summary });
      setPhase("done");
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="import-v2-dialog">
        <DialogHeader>
          <DialogTitle>Importazione atleti · sistema intelligente</DialogTitle>
          <DialogDescription>
            2 fasi: analisi (riconosce esistenti, previene duplicati) → conferma.
            Lo stesso file può essere importato più volte senza creare doppioni.
          </DialogDescription>
        </DialogHeader>

        {phase === "upload" && (
          <div className="space-y-4">
            <input
              type="file"
              accept=".xlsx,.xls"
              data-testid="import-v2-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-black file:text-white hover:file:bg-zinc-800"
            />
            <p className="text-xs text-zinc-500">Il sistema riconosce atleti esistenti tramite: external_id, codice FIS, nome+cognome+data, email, telefono. Non vengono mai creati duplicati automaticamente.</p>
            <div className="flex justify-end">
              <Button data-testid="import-v2-analyze" onClick={doAnalyze} disabled={!file || busy} className="bg-black hover:bg-zinc-800">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analisi…</> : <><Sparkles className="w-4 h-4 mr-2" />Analizza</>}
              </Button>
            </div>
          </div>
        )}

        {phase === "analyze" && analysis && (
          <div className="space-y-4" data-testid="import-v2-preview">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
              <Stat l="Nuovi" v={analysis.stats.new} tone="emerald" />
              <Stat l="Da aggiornare" v={analysis.stats.update_missing} tone="blue" />
              <Stat l="Conflitti" v={analysis.stats.conflict} tone="amber" />
              <Stat l="Possibili duplicati" v={analysis.stats.possible_duplicate} tone="amber" />
              <Stat l="Incompleti" v={analysis.stats.ignored_incomplete} tone="zinc" />
              <Stat l="Totale righe" v={analysis.total_rows} tone="zinc" />
            </div>

            <div className="border border-zinc-200 rounded-md max-h-[50vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Riga</th>
                    <th className="text-left px-3 py-2">Match</th>
                    <th className="text-left px-3 py-2">Dati dal file</th>
                    <th className="text-left px-3 py-2">Differenze</th>
                    <th className="text-left px-3 py-2 w-52">Azione</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.rows.map((r) => {
                    const ml = MATCH_LABEL[r.match_type] || { l: r.match_type, c: "bg-zinc-100" };
                    return (
                      <tr key={r.row_id} data-testid={`row-${r.row_id}`} className="border-t border-zinc-100 align-top">
                        <td className="px-3 py-2 font-mono">#{r.excel_row}</td>
                        <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${ml.c}`}>{ml.l}</span></td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{r.file_data.first_name} {r.file_data.last_name}</div>
                          <div className="text-zinc-500 font-mono">{r.file_data.birth_date || "—"} · {r.file_data.email || "—"} · {r.file_data.phone || "—"}</div>
                        </td>
                        <td className="px-3 py-2">
                          {r.matched_athlete && (
                            <div className="text-[11px] text-zinc-600 mb-1">
                              → esistente: <strong>{r.matched_athlete.first_name} {r.matched_athlete.last_name}</strong>
                              {r.matched_athlete.birth_date && <span className="font-mono ml-1">({r.matched_athlete.birth_date})</span>}
                            </div>
                          )}
                          {(r.diffs || []).length === 0 && !r.matched_athlete && <span className="text-zinc-400">—</span>}
                          {(r.diffs || []).map((d, i) => (
                            <div key={i} className="text-[11px]">
                              <span className="font-mono text-zinc-500">{d.field}:</span>{" "}
                              <span className={d.state === "conflict" ? "text-amber-700" : "text-emerald-700"}>
                                {d.state === "will_fill" ? "vuoto" : String(d.db)} → {String(d.file)}
                              </span>
                              {d.state === "conflict" && <AlertTriangle className="w-3 h-3 inline text-amber-600 ml-1" />}
                            </div>
                          ))}
                        </td>
                        <td className="px-3 py-2">
                          <Select value={decisions[r.row_id] || "ignore"} onValueChange={(v) => setDecisions((s) => ({ ...s, [r.row_id]: v }))}>
                            <SelectTrigger data-testid={`action-${r.row_id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ACTION_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={reset} disabled={busy}>Ricomincia</Button>
              <Button data-testid="import-v2-confirm" onClick={doConfirm} disabled={busy} className="bg-black hover:bg-zinc-800">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvataggio…</> : <><Check className="w-4 h-4 mr-2" />Conferma e salva</>}
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === "done" && analysis?.summary && (
          <div className="space-y-3" data-testid="import-v2-done">
            <div className="text-center py-4">
              <Check className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
              <h3 className="font-heading font-semibold text-xl">Importazione completata</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              <Stat l="Nuovi atleti" v={analysis.summary.new} tone="emerald" />
              <Stat l="Atleti aggiornati" v={analysis.summary.updated} tone="blue" />
              <Stat l="Campi completati" v={analysis.summary.fields_filled} tone="emerald" />
              <Stat l="Duplicati evitati" v={analysis.summary.duplicates_prevented} tone="amber" />
              <Stat l="Da verificare" v={analysis.summary.verify} tone="amber" />
              <Stat l="Ignorati / errori" v={(analysis.summary.ignored || 0) + (analysis.summary.errors?.length || 0)} tone="zinc" />
            </div>
            <DialogFooter>
              <Button onClick={() => { reset(); onOpenChange(false); }} className="bg-black hover:bg-zinc-800">Chiudi</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ l, v, tone }) {
  const c = { emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    zinc: "bg-zinc-50 text-zinc-700 border-zinc-200" };
  return (
    <div className={`border rounded p-2 ${c[tone] || c.zinc}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{l}</div>
      <div className="text-lg font-heading font-bold">{v ?? 0}</div>
    </div>
  );
}
